/**
 * TransitionSettingsModal Component
 *
 * Modal for configuring transition screen settings from the PreTrading dashboard.
 * Includes duration, auto-advance, and quote library management.
 * Note: Enable/disable is managed via toggle on the dashboard card, not in this modal.
 *
 * Key behaviors:
 * - Manual Save/Discard pattern
 * - Dirty-close blocking: when dirty, backdrop/X/Esc are blocked
 * - SortableList for drag-and-drop quote reordering
 * - Inline editing with validation
 * - Reset to Defaults action for quote library
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Plus, Trash2, RotateCcw } from 'lucide-react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Switch,
  Card,
  CardBody,
  SortableList,
  DragHandle,
  EmptyState,
  useToastNotification,
} from '@enfomentis/ui';
import type { DragHandleProps } from '@enfomentis/ui';
import { generateUUID } from '@/utils';
import { DEFAULT_TRANSITION_QUOTES } from '@/constants/preTrading';
import type { TransitionQuote } from '@/types/schema/PreTradingSettings';

// ============================================================================
// Types
// ============================================================================

export interface TransitionSettingsModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Async callback when settings are saved */
  onSave: (settings: {
    transitionDuration: number;
    autoAdvance: boolean;
    transitionQuotes: TransitionQuote[];
  }) => Promise<void>;
  /** Initial duration in seconds */
  initialTransitionDuration: number;
  /** Initial auto-advance state */
  initialAutoAdvance: boolean;
  /** Initial quotes from settings */
  initialQuotes: TransitionQuote[];
}

/**
 * Local state shape for all settings
 */
interface LocalConfig {
  transitionDuration: number;
  autoAdvance: boolean;
  quotes: TransitionQuote[];
}

// ============================================================================
// Component
// ============================================================================

export const TransitionSettingsModal: React.FC<TransitionSettingsModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialTransitionDuration,
  initialAutoAdvance,
  initialQuotes,
}) => {
  const { showToast } = useToastNotification();

  // Local editing state
  const [localConfig, setLocalConfig] = useState<LocalConfig>({
    transitionDuration: initialTransitionDuration,
    autoAdvance: initialAutoAdvance,
    quotes: [],
  });
  const [isSaving, setIsSaving] = useState(false);

  // Per-quote validation errors (keyed by quote id)
  const [quoteValidationErrors, setQuoteValidationErrors] = useState<Record<string, string>>({});

  // Duration validation error
  const [durationError, setDurationError] = useState<string | null>(null);

  // Baseline for dirty tracking (stores JSON string)
  const baselineKeyRef = useRef<string>('');

  // Track previous isOpen state to detect open transition (false → true)
  const wasOpenRef = useRef(false);

  // Track newly added quote ID for auto-focus
  const newlyAddedIdRef = useRef<string | null>(null);

  // Clear auto-focus ref after render
  useEffect(() => {
    if (newlyAddedIdRef.current === null) return;

    const timeoutId = setTimeout(() => {
      newlyAddedIdRef.current = null;
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [localConfig.quotes]);

  // Compute dirty state using stable JSON comparison
  const localConfigKey = useMemo(() => JSON.stringify(localConfig), [localConfig]);
  const isDirty = localConfigKey !== baselineKeyRef.current;

  // Check if there are any validation errors
  const hasQuoteErrors = Object.keys(quoteValidationErrors).length > 0;
  const hasValidationErrors = hasQuoteErrors || durationError !== null;

  // Initialize local state when modal opens
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      // Deep copy quotes
      const quotesCopy: TransitionQuote[] = initialQuotes.map((q) => ({ ...q }));

      const config: LocalConfig = {
        transitionDuration: initialTransitionDuration,
        autoAdvance: initialAutoAdvance,
        quotes: quotesCopy,
      };

      setLocalConfig(config);
      baselineKeyRef.current = JSON.stringify(config);

      // Reset ALL state to prevent stale UI
      setQuoteValidationErrors({});
      setDurationError(null);
      setIsSaving(false);
      newlyAddedIdRef.current = null;
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, initialTransitionDuration, initialAutoAdvance, initialQuotes]);

  // ============================================================================
  // Duration Validation
  // ============================================================================

  const validateDuration = useCallback((value: number): string | null => {
    if (value < 1) {
      return 'Must be at least 1 second';
    }
    if (!Number.isInteger(value)) {
      return 'Must be a whole number';
    }
    return null;
  }, []);

  // ============================================================================
  // Preferences Handlers
  // ============================================================================

  const handleDurationChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;
      const numValue = parseInt(rawValue, 10);

      // Handle empty or invalid input
      if (rawValue === '' || isNaN(numValue)) {
        setLocalConfig((prev) => ({ ...prev, transitionDuration: 0 }));
        setDurationError('Must be at least 1 second');
        return;
      }

      setLocalConfig((prev) => ({ ...prev, transitionDuration: numValue }));
      setDurationError(validateDuration(numValue));
    },
    [validateDuration]
  );

  const handleAutoAdvanceToggle = useCallback((isSelected: boolean) => {
    setLocalConfig((prev) => ({ ...prev, autoAdvance: isSelected }));
  }, []);

  // ============================================================================
  // Quote Handlers
  // ============================================================================

  /**
   * Handle inline text change with live validation
   */
  const handleQuoteTextChange = useCallback((id: string, text: string) => {
    setLocalConfig((prev) => ({
      ...prev,
      quotes: prev.quotes.map((q) => (q.id === id ? { ...q, text } : q)),
    }));

    // Clear validation error when user starts typing
    setQuoteValidationErrors((prev) => {
      if (prev[id]) {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return prev;
    });
  }, []);

  /**
   * Handle text blur - trim and validate
   */
  const handleQuoteTextBlur = useCallback((id: string, currentText: string) => {
    const trimmedText = currentText.trim();

    setLocalConfig((prev) => ({
      ...prev,
      quotes: prev.quotes.map((q) => (q.id === id ? { ...q, text: trimmedText } : q)),
    }));

    // Validate
    if (!trimmedText) {
      setQuoteValidationErrors((prev) => ({ ...prev, [id]: 'Quote text is required' }));
    } else {
      setQuoteValidationErrors((prev) => {
        if (!prev[id]) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  }, []);

  /**
   * Handle inline author change
   */
  const handleQuoteAuthorChange = useCallback((id: string, author: string) => {
    setLocalConfig((prev) => ({
      ...prev,
      quotes: prev.quotes.map((q): TransitionQuote => {
        if (q.id !== id) return q;
        // Omit author property entirely when empty (exactOptionalPropertyTypes)
        if (author) {
          return { id: q.id, text: q.text, author };
        }
        return { id: q.id, text: q.text };
      }),
    }));
  }, []);

  /**
   * Handle author blur - trim and normalize to undefined if empty
   */
  const handleQuoteAuthorBlur = useCallback((id: string, currentAuthor: string) => {
    const trimmedAuthor = currentAuthor.trim();

    setLocalConfig((prev) => ({
      ...prev,
      quotes: prev.quotes.map((q): TransitionQuote => {
        if (q.id !== id) return q;
        // Omit author property entirely when empty (exactOptionalPropertyTypes)
        if (trimmedAuthor) {
          return { id: q.id, text: q.text, author: trimmedAuthor };
        }
        return { id: q.id, text: q.text };
      }),
    }));
  }, []);

  /**
   * Add new empty quote with auto-focus
   */
  const handleAddQuote = useCallback(() => {
    const newId = generateUUID();
    const newQuote: TransitionQuote = {
      id: newId,
      text: '',
    };

    setLocalConfig((prev) => ({
      ...prev,
      quotes: [...prev.quotes, newQuote],
    }));

    // Track for auto-focus
    newlyAddedIdRef.current = newId;

    // Add validation error for empty text
    setQuoteValidationErrors((prev) => ({ ...prev, [newId]: 'Quote text is required' }));
  }, []);

  /**
   * Delete quote immediately (no confirmation)
   */
  const handleDeleteQuote = useCallback((id: string) => {
    setLocalConfig((prev) => ({
      ...prev,
      quotes: prev.quotes.filter((q) => q.id !== id),
    }));

    // Clear any validation error for deleted quote
    setQuoteValidationErrors((prev) => {
      if (prev[id]) {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return prev;
    });
  }, []);

  /**
   * Handle reorder from SortableList
   */
  const handleReorder = useCallback((reorderedQuotes: TransitionQuote[]) => {
    setLocalConfig((prev) => ({
      ...prev,
      quotes: reorderedQuotes,
    }));
  }, []);

  /**
   * Reset quotes to defaults
   */
  const handleResetToDefaults = useCallback(() => {
    // Clone default quotes to avoid shared references
    const defaultQuotes: TransitionQuote[] = DEFAULT_TRANSITION_QUOTES.map((q) => ({ ...q }));

    setLocalConfig((prev) => ({
      ...prev,
      quotes: defaultQuotes,
    }));

    // Clear all quote validation errors
    setQuoteValidationErrors({});
  }, []);

  // ============================================================================
  // Save/Discard Handlers
  // ============================================================================

  /**
   * Validate all and save if valid
   */
  const handleSave = useCallback(async () => {
    // Validate duration
    const durError = validateDuration(localConfig.transitionDuration);
    if (durError) {
      setDurationError(durError);
    }

    // Validate all quotes (trim text)
    const quoteErrors: Record<string, string> = {};
    const trimmedQuotes: TransitionQuote[] = localConfig.quotes.map((q): TransitionQuote => {
      const trimmedText = q.text.trim();
      const trimmedAuthor = q.author?.trim();
      if (!trimmedText) {
        quoteErrors[q.id] = 'Quote text is required';
      }
      // Omit author property entirely when empty (exactOptionalPropertyTypes)
      if (trimmedAuthor) {
        return { id: q.id, text: trimmedText, author: trimmedAuthor };
      }
      return { id: q.id, text: trimmedText };
    });

    // If any errors, update state and abort save
    if (durError || Object.keys(quoteErrors).length > 0) {
      setQuoteValidationErrors(quoteErrors);
      setLocalConfig((prev) => ({ ...prev, quotes: trimmedQuotes }));
      return;
    }

    setIsSaving(true);

    try {
      await onSave({
        transitionDuration: localConfig.transitionDuration,
        autoAdvance: localConfig.autoAdvance,
        transitionQuotes: trimmedQuotes,
      });

      // On successful save: update baseline (clears dirty state), then close
      const savedConfig = { ...localConfig, quotes: trimmedQuotes };
      baselineKeyRef.current = JSON.stringify(savedConfig);
      onClose();
    } catch (err) {
      console.error('Save error:', err);
      showToast({
        title: 'Save failed',
        description: 'An unexpected error occurred. Please try again.',
        color: 'danger',
      });
    } finally {
      setIsSaving(false);
    }
  }, [localConfig, validateDuration, onSave, onClose, showToast]);

  /**
   * Discard changes and close
   */
  const handleDiscard = useCallback(() => {
    const baseline = JSON.parse(baselineKeyRef.current) as LocalConfig;
    setLocalConfig(baseline);
    setQuoteValidationErrors({});
    setDurationError(null);
    onClose();
  }, [onClose]);

  // ============================================================================
  // Quote Row Renderer
  // ============================================================================

  const renderQuoteRow = useCallback(
    (quote: TransitionQuote, dragHandleProps: DragHandleProps) => {
      const error = quoteValidationErrors[quote.id];
      const shouldAutoFocus = newlyAddedIdRef.current === quote.id;

      return (
        <Card
          shadow="sm"
          radius="sm"
          className={dragHandleProps.isDragging ? 'opacity-50 shadow-lg' : ''}
        >
          <CardBody className="p-3">
            <div className="flex items-start gap-3">
              <div className="pt-2">
                <DragHandle dragHandleProps={dragHandleProps} />
              </div>

              {/* Quote fields */}
              <div className="flex-1 min-w-0 space-y-2">
                <Input
                  size="sm"
                  placeholder="Enter quote text..."
                  value={quote.text}
                  onChange={(e) => handleQuoteTextChange(quote.id, e.target.value)}
                  onBlur={(e) => handleQuoteTextBlur(quote.id, e.target.value)}
                  isInvalid={!!error}
                  errorMessage={error}
                  autoFocus={shouldAutoFocus}
                  classNames={{ input: 'text-base' }}
                />
                <Input
                  size="sm"
                  placeholder="Author (optional)"
                  value={quote.author ?? ''}
                  onChange={(e) => handleQuoteAuthorChange(quote.id, e.target.value)}
                  onBlur={(e) => handleQuoteAuthorBlur(quote.id, e.target.value)}
                  classNames={{ input: 'text-sm text-default-500' }}
                />
              </div>

              <Button
                isIconOnly
                size="sm"
                variant="light"
                color="danger"
                onPress={() => handleDeleteQuote(quote.id)}
                aria-label={`Delete quote`}
                className="mt-1"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </CardBody>
        </Card>
      );
    },
    [
      quoteValidationErrors,
      handleQuoteTextChange,
      handleQuoteTextBlur,
      handleQuoteAuthorChange,
      handleQuoteAuthorBlur,
      handleDeleteQuote,
    ]
  );

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      placement="top"
      scrollBehavior="inside"
      size="2xl"
      isDismissable={!isDirty}
      hideCloseButton={isDirty}
      isKeyboardDismissDisabled={isDirty}
    >
      <ModalContent>
        <ModalHeader>
          <span className="text-lg font-semibold">Transition Screen Settings</span>
        </ModalHeader>

        <ModalBody className="gap-6">
          {/* Duration Input */}
          <Input
            type="number"
            label="Duration (seconds)"
            description="How long the transition screen is displayed"
            value={localConfig.transitionDuration.toString()}
            onChange={handleDurationChange}
            min={1}
            isInvalid={!!durationError}
            errorMessage={durationError}
            classNames={{ input: 'text-base' }}
          />

          {/* Auto-Advance Toggle */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1">
              <p className="text-sm font-medium">Auto-Advance</p>
              <p className="text-sm text-default-500">
                Automatically proceed after duration expires (otherwise requires click)
              </p>
            </div>
            <Switch
              isSelected={localConfig.autoAdvance}
              onValueChange={handleAutoAdvanceToggle}
              aria-label="Auto-advance after duration"
            />
          </div>

          {/* Divider */}
          <div className="border-t border-default-200" />

          {/* Quote Library Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Quote Library</p>
                <p className="text-sm text-default-500">
                  Motivational quotes displayed during the transition
                </p>
              </div>
              <Button
                variant="flat"
                size="sm"
                startContent={<RotateCcw className="w-4 h-4" />}
                onPress={handleResetToDefaults}
              >
                Reset to Defaults
              </Button>
            </div>

            {/* Quotes List */}
            {localConfig.quotes.length > 0 ? (
              <SortableList
                items={localConfig.quotes}
                onReorder={handleReorder}
                renderItem={renderQuoteRow}
                className="space-y-2"
              />
            ) : (
              <EmptyState
                title="No quotes configured"
                description="Add quotes to display during your transition screen, or reset to defaults."
              />
            )}

            {/* Add Quote Button */}
            <Button
              variant="flat"
              color="primary"
              startContent={<Plus className="w-4 h-4" />}
              onPress={handleAddQuote}
              className="w-full md:w-auto"
            >
              Add Quote
            </Button>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button
            variant="light"
            onPress={isDirty ? handleDiscard : onClose}
            isDisabled={isSaving}
          >
            {isDirty ? 'Discard' : 'Cancel'}
          </Button>
          <Button
            color="primary"
            onPress={handleSave}
            isDisabled={!isDirty || hasValidationErrors || isSaving}
            isLoading={isSaving}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
